import React from 'react';
import PropTypes from 'prop-types';
import Button from '~/components/Button';

import BaseDialog from './BaseDialog';
import { mui } from '~/tools/mui';
import { jss } from '~/tools/jss';
import { useTranslation } from '~/behaviors';

const useStyles = jss.makeStyles((theme) => ({
  content: {
    minWidth: 450,
    maxWidth: 600,
    display: 'flex',
  },
  iconContainer: {
    width: 64,
    height: 60,
    marginRight: 24,
  },
  iconPositioner: {
    width: 60,
    height: 60,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '&.with-small-icon': {
      border: `1px solid ${theme.palette.primary.main}`,
      position: 'relative',
      borderRadius: 60,
    },
  },
  icon: {
    fontSize: 42,
    color: theme.palette.primary.main,
  },
  smallIconContainer: {
    width: 23,
    height: 23,
    border: '2px solid white',
    borderRadius: 23,
    backgroundColor: theme.palette.primary.main,
    position: 'absolute',
    bottom: 0,
    right: -4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  smallIcon: {
    color: 'white',
    fontSize: 15,
    paddingLeft: 2,
    transform: 'rotate(-45deg)',
  },
  typographyContainer: {
    padding: '4px 0 8px 0',
  },
  title: {
    fontSize: 20,
    fontWeight: 500,
    marginBottom: 8,
  },
  message: {
    fontSize: 16,
  },
}));

// REFACTOR: it's confusing that title, message, and button label are not the
// actual string values, but the POEditor translation key values. Perhaps these
// should simply be plain strings, and the values should be translated in the
// context where they are assigned (higher level)?
//
// e.g.,
// ```
// <SimpleDialog
//   title={t('whatever i want')}
//   description='a plain string if that is all i need'
//   closeButtonLabel={t('button.close')}
// />
// ```

const SimpleDialog = ({
  title,
  message,
  isVisible,
  Icon = null,
  SmallIcon = null,
  onClose,
  onConfirm = null,
  closeButtonLabel = 'button.ok', // REFACTOR: would rather this be a plain string than a POEditor key
  allowCancel = false,
  cancelButtonLabel = 'button.cancel',
}) => {
  // @ts-expect-error TS(2339): Property 'propTypes' does not exist on type '({ ti... Remove this comment to see the full error message
  SimpleDialog.propTypes = {
    title: PropTypes.string,
    message: PropTypes.string.isRequired,
    isVisible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func,
    Icon: PropTypes.elementType,
    SmallIcon: PropTypes.elementType,
    closeButtonLabel: PropTypes.string,
    allowCancel: PropTypes.bool,
    cancelButtonLabel: PropTypes.string,
  };

  const styles = useStyles();
  const { t } = useTranslation();

  const TitleSection = () => (
    <div className={styles.content}>
      {Icon && (
        <div className={styles.iconContainer}>
          <div
            className={jss.combineStyles(styles.iconPositioner, {
              'with-small-icon': !!SmallIcon,
            })}
          >
            {/*
              // @ts-expect-error TS(2604): JSX element type 'Icon' does not have any construc... Remove this comment to see the full error message */}
            <Icon className={styles.icon} />
            {SmallIcon && (
              <div className={styles.smallIconContainer}>
                {/*
                  // @ts-expect-error TS(2604): JSX element type 'SmallIcon' does not have any con... Remove this comment to see the full error message */}
                <SmallIcon className={styles.smallIcon} />
              </div>
            )}
          </div>
        </div>
      )}
      <div className={styles.typographyContainer}>
        {title && (
          <mui.Typography variant="h1" className={styles.title} data-testid="dialog-title">
            {t(title)}
          </mui.Typography>
        )}
        <mui.Typography variant="h5" className={styles.message} data-testid="dialog-message">
          {message}
        </mui.Typography>
      </div>
    </div>
  );

  const CloseButton = (e) => (
    <>
      {allowCancel && (
        <Button onClick={onClose} color="primary" variant="text" data-testid="dialog-cancel-button">
          {t(cancelButtonLabel)}
        </Button>
      )}
      <Button onClick={onConfirm || onClose} color="primary" variant="contained" data-testid="dialog-close-button">
        {t(closeButtonLabel)}
      </Button>
    </>
  );

  return <BaseDialog isVisible={isVisible} onClose={onClose} Title={TitleSection} Actions={CloseButton} />;
};

export default SimpleDialog;
